// @story HIR-HR8
// The EquaOS assistant on the school's Hiring app (Shohini, 9 Oct 2026: one
// assistant, not two). It is the same assistant as every other EquaOS app,
// working on Hiring's own MCP tools with the person's token: find and count,
// move a candidate, message one, draft a vacancy (the Vacancy Writer), change
// a pipeline or write a stage's questions (the Assessment Designer). Every
// change comes back as a card to Approve or Cancel; removing pipeline stages
// needs a tick on each one first.
//
// The service's client (assistantClient.js) renders under its own React root
// in the div below, so Hiring's React tree and the assistant's never mix.
// Until the service answers (not deployed yet, offline) nothing is shown.

import React, { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import api from '../../services/api';
import { useCopilot } from '../copilot/CopilotContext';
import { ADMIN_PAGES, HIRING_PAGES } from '../../dashboard/adminPages';
import { loadAssistantClient } from './assistantClient';
import { freshAccessToken } from './assistantToken';

const GREETING = 'Ask me to find something or get a hiring task done: vacancies, candidates, pipelines, question papers and messages. Type, or tap the mic and speak in English, Hindi or Marathi. Anything that changes something comes back to you as a card to approve first.';
const SUGGESTIONS = [
  'How many applications does each vacancy have?',
  "Show today's hiring summary",
  'Who has not paid the application fee?',
];

const MAX_ITEMS = 8;

/* The page the person is on, in their words. */
export function pageLabel(pathname) {
  const p = String(pathname || '/');
  const own = [...HIRING_PAGES, ...ADMIN_PAGES].find((x) => p === x.value || p.startsWith(`${x.value}/`));
  if (/^\/jobs\/[^/]+\/human-interview\//.test(p)) return 'Interview panel';
  if (/^\/jobs\/[^/]+/.test(p) && !/^\/jobs\/(create|generate)\/?$/.test(p)) return 'Vacancy';
  return own ? own.label : 'Hiring';
}

/* What is on screen, for the assistant: { page, items: [{ kind, label, id }] }. */
export function contextFor({ pathname, vacancy, focus }) {
  const items = [];
  if (vacancy && vacancy.id) items.push({ kind: 'Vacancy', label: vacancy.title || 'This vacancy', id: vacancy.id });
  if (focus && focus.stageId) items.push({ kind: 'Stage', label: focus.stageLabel || 'This stage', id: focus.stageId });
  if (focus && focus.candidateId) items.push({ kind: 'Candidate', label: focus.candidateName || 'This candidate', id: focus.candidateId });
  return { page: pageLabel(pathname), items: items.slice(0, MAX_ITEMS) };
}

const vacancyIdOf = (pathname) => {
  const m = /^\/jobs\/([0-9a-f]{24})(?:\/|$)/i.exec(String(pathname || ''));
  return m ? m[1] : null;
};

/* The vacancy on screen, with its title (read once per vacancy). */
function useVacancyOnScreen(pathname) {
  const id = vacancyIdOf(pathname);
  const [vacancy, setVacancy] = useState(null);
  useEffect(() => {
    if (!id) { setVacancy(null); return undefined; }
    let gone = false;
    setVacancy({ id, title: '' });
    api.get(`/jobs/${id}`)
      .then((res) => {
        const d = res.data || {};
        const title = d.data?.title || d.job?.title || d.title || '';
        if (!gone) setVacancy({ id, title });
      })
      .catch(() => { /* the id alone still helps */ });
    return () => { gone = true; };
  }, [id]);
  return vacancy;
}

export default function SharedAssistant() {
  const host = useRef(null);
  const location = useLocation();
  const copilot = useCopilot();          // pages still say what is on screen through it
  const vacancy = useVacancyOnScreen(location.pathname);
  const [unavailable, setUnavailable] = useState(false);

  // Read at the moment the assistant asks, so it always sees the current page.
  const onScreen = useRef({});
  onScreen.current = { pathname: location.pathname, vacancy, focus: copilot?.focus || null };

  useEffect(() => {
    let gone = false;
    let panel = null;
    loadAssistantClient()
      .then((client) => {
        if (gone || !host.current) return;
        const respond = client.equallRespond({
          module: 'hiring',
          getToken: freshAccessToken,
          getContext: () => contextFor(onScreen.current),
        });
        const voice = typeof client.createVoiceInput === 'function'
          ? client.createVoiceInput({ apiBase: '/api', getToken: freshAccessToken })
          : undefined;
        panel = client.mountAssistant(host.current, {
          respond, voice, greeting: GREETING, suggestions: SUGGESTIONS,
        });
      })
      .catch(() => { if (!gone) setUnavailable(true); });
    return () => {
      gone = true;
      try { panel?.unmount?.(); } catch (e) { /* already gone */ }
    };
  }, []);

  if (unavailable) return null;
  return <div ref={host} className="equaos-assistant-host" data-testid="equaos-assistant" />;
}
